import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import SEO from "components/SEO.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import { SectionHeading } from "components/misc/Headings";
import articleSecondImg from "images/article_second.jpeg";
import complianceImg1 from "assets/compiance_img_1.png";

const HeadingRow = tw.div`flex justify-center`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10 text-center`;

const IntroSection = tw.div`mb-12`;
const IntroImage = tw.img`w-full max-w-md mx-auto rounded-lg shadow-md mb-8`;
const IntroText = tw.p`text-lg text-gray-700 leading-relaxed mb-6`;

const ContentSection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;
const SectionTitle = tw.h2`text-2xl font-bold text-gray-900 mb-6 flex items-center`;
const SectionSubtitle = tw.h3`text-xl font-bold text-gray-800 mb-4`;
const SectionIcon = tw.span`mr-3 text-3xl`;
const SectionContent = tw.div`text-gray-700 leading-relaxed`;
const SectionImage = tw.img`w-full max-w-md mx-auto rounded-lg shadow-md my-6`;
const PenaltySection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;
const BeyondFinesSection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;
const ImportanceSection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;

const AtGlanceSection = tw.div`mt-16 mb-12 rounded-lg p-8`;
const AtGlanceTitle = tw.h2`text-2xl font-bold text-gray-900 mb-4 flex items-center`;

const ConclusionSection = styled.div`
  ${tw`mt-16 p-8 rounded-lg`}
  background-color: #314f68;
`;
const ConclusionTitle = tw.h2`text-3xl font-bold text-white mb-6`;
const ConclusionText = tw.p`text-lg text-white leading-relaxed mb-4`;

const FlexContainer = styled.div`
  ${tw`flex items-center mb-8`}
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;
const ImageContainer = styled.div`
  ${tw`flex-shrink-0`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`;
const ContentContainer = styled.div`
  ${tw`flex-1`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const Text = styled.div`
  ${tw`text-lg text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside ml-6`}
    li {
      ${tw`mb-3 text-gray-700`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
  strong {
    ${tw`font-semibold text-gray-900`}
  }
`;
export default ({ headingText = "POSH Act: Penalties for Non-Compliance" }) => {
    return (
        <AnimationRevealPage>
            <SEO
                title="POSH Act: Penalties for Non-Compliance - SEQUILERN"
                description="Learn about the strict penalties and consequences for non-compliance with the POSH Act 2013. Understand fines, additional consequences, and why workplace safety compliance matters."
                keywords="POSH Act penalties, non-compliance, workplace safety, sexual harassment law, fines, compliance requirements, legal consequences"
                url="https://www.sequilern.com/penalties-for-non-compliance"
            />
            <Header />
            <Container>
                <ContentWithPaddingXl>
                    <HeadingRow>
                        <Heading>{headingText}</Heading>
                    </HeadingRow>
                    
                    {/* Introduction Section */}
                    <IntroSection>
                        <IntroText>
                            The POSH Act, 2013 not only prescribes preventive and redressal mechanisms for workplace sexual
                            harassment but also enforces strict penalties to ensure accountability. These provisions make compliance
                            a legal obligation and not just a matter of policy.
                        </IntroText>
                    </IntroSection>

                    {/* Key Penalties Section */}
                    <PenaltySection>
                        <SectionTitle>
                            <SectionIcon>⚖️</SectionIcon>
                            Key Penalties under Section 26
                        </SectionTitle>
                        <Text>
                            <ul>
                                <li>
                                    <strong>Failure to constitute an Internal Committee (IC), act on its recommendations, or comply with any
                                    provision of the Act can lead to a fine of up to ₹50,000.</strong>
                                </li>
                                <li>
                                    <strong>For repeat violations, penalties may double, and authorities can suspend or cancel the
                                    organization's license/registration.</strong>
                                </li>
                            </ul>
                        </Text>
                    </PenaltySection>

                    {/* Beyond Fines Section with Image */}
                    <BeyondFinesSection>
                        <SectionTitle>
                            <SectionIcon>📋</SectionIcon>
                            Beyond Fines: Additional Consequences
                        </SectionTitle>
                        <FlexContainer>
                            <ContentContainer>
                                <Text>
                                    <ul>
                                        <li>
                                            <strong>Annual Reporting (Section 22):</strong> Employers must disclose case details in annual reports or to the
                                            District Officer. Non-disclosure risks statutory action and reputational damage.
                                        </li>
                                        <li>
                                            <strong>Confidentiality (Sections 16 & 17):</strong> Breaching confidentiality of the complainant, respondent, or
                                            witnesses attracts penalties under service rules and additional liability.
                                        </li>
                                        <li>
                                            <strong>Reputational & Governance Impact:</strong> Non-compliance can erode employee trust, invite
                                            regulatory scrutiny, and undermine ESG and investor confidence.
                                        </li>
                                        <li>
                                            <strong>Criminal Liability:</strong> Certain breaches may also trigger provisions under the Bharatiya Nyaya
                                            Sanhita, 2023.
                                        </li>
                                    </ul>
                                </Text>
                            </ContentContainer>
                            <ImageContainer>
                                <SectionImage src={complianceImg1} alt="POSH Act Additional Consequences" />
                            </ImageContainer>
                        </FlexContainer>
                    </BeyondFinesSection>

                    {/* Why It Matters Section */}
                    <ImportanceSection>
                        <SectionTitle>
                            <SectionIcon>💡</SectionIcon>
                            Why It Matters
                        </SectionTitle>
                        <Text>
                            <p>
                                The penalties are corrective, not just punitive—linking compliance to an organization's right to operate.
                                The law reinforces that workplace safety is a non-negotiable aspect of business accountability, echoing
                                the Supreme Court's stance in Vishaka and Medha Kotwal.
                            </p>
                        </Text>
                    </ImportanceSection>

                    {/* For Employers in 2025 Section */}
                    <ContentSection>
                        <SectionTitle>
                            <SectionIcon>🏢</SectionIcon>
                            For Employers in 2025
                        </SectionTitle>
                        <Text>
                            <p>Compliance should be treated as part of core business practice. This means:</p>
                            <ul>
                                <li>Constituting functional ICs,</li>
                                <li>Training employees regularly,</li>
                                <li>Supporting victims effectively, and</li>
                                <li>Documenting preventive efforts.</li>
                            </ul>
                        </Text>
                    </ContentSection>

                    {/* Conclusion */}
                    <ConclusionSection>
                        <ConclusionText>
                            Ultimately, penalties under the POSH Act are more than numbers—they safeguard women's dignity,
                            promote equality, and determine whether an organization retains both its credibility and legal right to
                            function.
                        </ConclusionText>
                        <ConclusionText>
                            <strong>Compliance is not optional—it's fundamental to operating a responsible, legally sound business
                            that prioritizes workplace safety and dignity for all employees.</strong>
                        </ConclusionText>
                    </ConclusionSection>

                    {/* At a Glance Section */}
                    <AtGlanceSection>
                        <AtGlanceTitle>
                            <SectionIcon>📋</SectionIcon>
                            At a Glance
                        </AtGlanceTitle>
                        <IntroImage src={articleSecondImg} alt="POSH Act Penalties Overview" />
                    </AtGlanceSection>
                </ContentWithPaddingXl>
            </Container>
            <Footer />
        </AnimationRevealPage>
    );
};

